import { useEffect, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { createPortal } from "react-dom";
import { formatFileSize } from "../lib/jobForm";
import {
  attachmentKind,
  attachmentSrc,
  formatAttachmentDate,
  isPlayableAudio,
  isViewableImage,
} from "../lib/attachmentKind";
import type { AttachmentDraft } from "../types";
import { AttachmentGallery } from "./AttachmentGallery";
import { WaveformPlayer } from "./WaveformPlayer";
import form from "./NewJobForm.module.css";
import styles from "./AttachmentsPage.module.css";

/**
 * What the file picker offers. Wide by design — a job's evidence arrives as
 * photos, scanned work orders, spreadsheets of measurements, and voice notes
 * from site. The server accepts any type; this list only steers the dialog.
 */
export const ATTACHMENT_ACCEPT =
  "image/*,video/*,audio/*,application/pdf,.doc,.docx,.odt,.rtf,.xls,.xlsx,.xlsm,.csv,.ods,.ppt,.pptx,.txt,.zip";

/** Grid (thumbnails) or list (one row per file). */
type ViewMode = "grid" | "list";

export interface AttachmentsPageProps {
  attachments: readonly AttachmentDraft[];
  /** Persisted job id — present in edit mode, where files download from the API. */
  jobId?: string;
  onAdd: (files: FileList | null) => void;
  onRemove: (id: string) => void;
}

/**
 * The Attachments page — one surface for every file on the job.
 *
 * Shows exactly what the Details page holds (the same draft array; this is
 * a different view of it, not a second store), and adds what a details form has
 * no room for: thumbnails, a drop target, a detail popover per file, a list
 * alternative, and a full-screen viewer for photos.
 *
 * Files added here are staged in the draft like any other, so a new job uploads
 * them once the job row exists; on an existing job, removing a stored file
 * deletes it server-side, which is why removal is a two-step confirm.
 */
export function AttachmentsPage({ attachments, jobId, onAdd, onRemove }: AttachmentsPageProps) {
  const [view, setView] = useState<ViewMode>("grid");
  const [galleryIndex, setGalleryIndex] = useState<number | null>(null);
  const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  // dragenter/dragleave fire for every child element the pointer crosses;
  // counting them is what keeps the highlight from flickering mid-drop.
  const dragDepth = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const images = attachments.filter((attachment) => isViewableImage(attachment, jobId));
  const totalBytes = attachments.reduce((sum, attachment) => sum + attachment.size, 0);

  // Clearing the input is what makes re-picking the same file fire `change`
  // again; without it a removed attachment cannot be added back.
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    onAdd(event.target.files);
    event.target.value = "";
  };

  const handleDragEnter = (event: DragEvent): void => {
    if (!event.dataTransfer?.types.includes("Files")) return;
    dragDepth.current += 1;
    setDragging(true);
  };

  const handleDragLeave = (): void => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  };

  const handleDragOver = (event: DragEvent): void => {
    if (!event.dataTransfer?.types.includes("Files")) return;
    // Without both of these the browser navigates to the dropped file.
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (event: DragEvent): void => {
    event.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    onAdd(event.dataTransfer?.files ?? null);
  };

  const handleRemove = (id: string, persisted?: boolean): void => {
    // A staged file is only a row in this form; a stored one is a file on the
    // server, so that delete is confirmed before it happens.
    if (!persisted) {
      onRemove(id);
      return;
    }
    if (pendingRemoveId === id) {
      setPendingRemoveId(null);
      onRemove(id);
    } else {
      setPendingRemoveId(id);
    }
  };

  const openGallery = (attachment: AttachmentDraft): void => {
    const index = images.findIndex((candidate) => candidate.id === attachment.id);
    if (index >= 0) setGalleryIndex(index);
  };

  return (
    <section
      className={`${form.section} ${styles.root} ${dragging ? styles.rootDragging : ""}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div className={styles.head}>
        <div className={styles.headText}>
          <h3 className={form.sectionTitle}>Attachments</h3>
          <p className={styles.count}>
            {attachments.length === 0
              ? "No files yet"
              : `${attachments.length} file${attachments.length === 1 ? "" : "s"} · ${formatFileSize(totalBytes)}${
                  images.length ? ` · ${images.length} image${images.length === 1 ? "" : "s"}` : ""
                }`}
          </p>
        </div>

        <div className={styles.headActions}>
          <div className={styles.toggle} role="group" aria-label="Layout">
            <button
              type="button"
              className={view === "grid" ? styles.toggleActive : styles.toggleButton}
              aria-pressed={view === "grid"}
              onClick={() => setView("grid")}
            >
              <GridIcon />
              Grid
            </button>
            <button
              type="button"
              className={view === "list" ? styles.toggleActive : styles.toggleButton}
              aria-pressed={view === "list"}
              onClick={() => setView("list")}
            >
              <ListIcon />
              List
            </button>
          </div>
          <button type="button" className={styles.addButton} onClick={() => inputRef.current?.click()}>
            Add files
          </button>
        </div>
      </div>

      <input
        ref={inputRef}
        id="job-attachments-page"
        className={form.fileInput}
        type="file"
        multiple
        accept={ATTACHMENT_ACCEPT}
        onChange={handleInputChange}
      />

      {/*
       * The drop target is the whole page (the section handles the events), so
       * this box is the affordance that says so; it stays visible with files
       * present, because "where do I drop the next one" is a live question.
       */}
      <div className={dragging ? styles.dropzoneActive : styles.dropzone}>
        <UploadIcon />
        <p className={styles.dropTitle}>
          {dragging ? "Drop to attach" : "Drag files here"}
        </p>
        <p className={styles.dropNote}>
          Photos, PDFs, documents, spreadsheets, audio and video ·{" "}
          <button type="button" className={styles.dropLink} onClick={() => inputRef.current?.click()}>
            browse your device
          </button>
        </p>
        {!jobId && (
          <p className={styles.dropNote}>Files upload when the job is saved.</p>
        )}
      </div>

      {attachments.length > 0 &&
        (view === "grid" ? (
          <ul className={styles.grid}>
            {attachments.map((attachment) => (
              <AttachmentTile
                key={attachment.id}
                attachment={attachment}
                jobId={jobId}
                pendingRemove={pendingRemoveId === attachment.id}
                onOpen={openGallery}
                onRemove={handleRemove}
                onCancelRemove={() => setPendingRemoveId(null)}
              />
            ))}
          </ul>
        ) : (
          <ul className={styles.list}>
            {attachments.map((attachment) => (
              <AttachmentListRow
                key={attachment.id}
                attachment={attachment}
                jobId={jobId}
                pendingRemove={pendingRemoveId === attachment.id}
                onOpen={openGallery}
                onRemove={handleRemove}
                onCancelRemove={() => setPendingRemoveId(null)}
              />
            ))}
          </ul>
        ))}

      {galleryIndex !== null && (
        <AttachmentGallery
          images={images}
          startIndex={galleryIndex}
          jobId={jobId}
          onClose={() => setGalleryIndex(null)}
        />
      )}
    </section>
  );
}

interface AttachmentItemProps {
  attachment: AttachmentDraft;
  jobId?: string;
  /** True while this row is asking for a second click to confirm deletion. */
  pendingRemove: boolean;
  onOpen: (attachment: AttachmentDraft) => void;
  onRemove: (id: string, persisted?: boolean) => void;
  onCancelRemove: () => void;
}

/** One thumbnail in the grid: preview, two-line name, and its detail popover. */
function AttachmentTile({
  attachment,
  jobId,
  pendingRemove,
  onOpen,
  onRemove,
  onCancelRemove,
}: AttachmentItemProps) {
  const kind = attachmentKind(attachment.name, attachment.type);
  const viewable = isViewableImage(attachment, jobId);
  const downloadable = Boolean(attachment.persisted && jobId);
  const audioSrc = isPlayableAudio(attachment, jobId) ? attachmentSrc(attachment, jobId) : null;
  const { anchorProps, popover } = useDetailPopover(attachment, jobId);

  const preview = <AttachmentPreview attachment={attachment} jobId={jobId} />;

  return (
    <li className={styles.tile}>
      {audioSrc ? (
        <div className={styles.audioThumb} {...anchorProps}>
          <WaveformPlayer src={audioSrc} name={attachment.name} />
        </div>
      ) : viewable ? (
        <button
          type="button"
          className={styles.thumbButton}
          onClick={() => onOpen(attachment)}
          aria-label={`Open ${attachment.name} in the image viewer`}
          {...anchorProps}
        >
          {preview}
        </button>
      ) : downloadable ? (
        <a
          className={styles.thumbButton}
          href={`/api/jobs/${jobId}/attachments/${attachment.id}`}
          download={attachment.name}
          aria-label={`Download ${attachment.name}`}
          {...anchorProps}
        >
          {preview}
        </a>
      ) : (
        <div className={styles.thumbStatic} tabIndex={0} aria-label={attachment.name} {...anchorProps}>
          {preview}
        </div>
      )}

      <p className={styles.tileName} title={attachment.name}>
        {attachment.name}
      </p>
      <p className={styles.tileMeta}>
        <span className={styles.tileKind}>{kind.badge}</span>
        {formatFileSize(attachment.size)}
      </p>

      <button
        type="button"
        className={pendingRemove ? styles.tileRemoveArmed : styles.tileRemove}
        onClick={() => onRemove(attachment.id, attachment.persisted)}
        onBlur={onCancelRemove}
        aria-label={
          pendingRemove ? `Confirm deleting ${attachment.name}` : `Remove ${attachment.name}`
        }
        title={pendingRemove ? "Click again to delete this file" : "Remove"}
      >
        {pendingRemove ? "Delete?" : <CloseIcon />}
      </button>

      {!attachment.persisted && <span className={styles.pending}>Pending</span>}
      {popover}
    </li>
  );
}

/** One row in the list view: small preview, name, and the metadata columns. */
function AttachmentListRow({
  attachment,
  jobId,
  pendingRemove,
  onOpen,
  onRemove,
  onCancelRemove,
}: AttachmentItemProps) {
  const kind = attachmentKind(attachment.name, attachment.type);
  const viewable = isViewableImage(attachment, jobId);
  const audioSrc = isPlayableAudio(attachment, jobId) ? attachmentSrc(attachment, jobId) : null;
  const { anchorProps, popover } = useDetailPopover(attachment, jobId);

  return (
    <li className={styles.row}>
      {viewable ? (
        <button
          type="button"
          className={styles.rowThumbButton}
          onClick={() => onOpen(attachment)}
          aria-label={`Open ${attachment.name} in the image viewer`}
          {...anchorProps}
        >
          <AttachmentPreview attachment={attachment} jobId={jobId} compact />
        </button>
      ) : (
        <div className={styles.rowThumb} {...anchorProps}>
          <AttachmentPreview attachment={attachment} jobId={jobId} compact />
        </div>
      )}

      <div className={styles.rowMain}>
        {attachment.persisted && jobId ? (
          <a
            className={styles.rowName}
            href={`/api/jobs/${jobId}/attachments/${attachment.id}`}
            download={attachment.name}
            {...anchorProps}
          >
            {attachment.name}
          </a>
        ) : (
          <span className={styles.rowName} {...anchorProps}>
            {attachment.name}
          </span>
        )}
        <span className={styles.rowSub}>
          {kind.label} · {formatFileSize(attachment.size)}
        </span>
        {audioSrc && (
          <div className={styles.rowAudio}>
            <WaveformPlayer src={audioSrc} name={attachment.name} compact />
          </div>
        )}
      </div>

      <span className={styles.rowMeta}>
        {attachment.persisted ? formatAttachmentDate(attachment.uploadedAt) : "Not uploaded yet"}
      </span>
      <span className={styles.rowMeta}>{attachment.uploadedBy || "—"}</span>

      <button
        type="button"
        className={pendingRemove ? styles.rowRemoveArmed : styles.rowRemove}
        onClick={() => onRemove(attachment.id, attachment.persisted)}
        onBlur={onCancelRemove}
      >
        {pendingRemove ? "Confirm delete" : "Remove"}
      </button>
      {popover}
    </li>
  );
}

/** Image preview when there is one, a file-type badge when there is not. */
function AttachmentPreview({
  attachment,
  jobId,
  compact = false,
}: {
  attachment: AttachmentDraft;
  jobId?: string;
  compact?: boolean;
}) {
  const kind = attachmentKind(attachment.name, attachment.type);
  const source = kind.family === "image" ? attachmentSrc(attachment, jobId) : null;
  // A stored image can 404 (file pruned, legacy metadata-only row); falling
  // back to the icon keeps a broken-image glyph out of the grid.
  const [failed, setFailed] = useState(false);

  if (source && !failed) {
    return (
      <img
        className={styles.previewImage}
        src={source}
        alt=""
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <span
      className={`${styles.icon} ${styles[`icon_${kind.family}`]} ${compact ? styles.iconCompact : ""}`}
      aria-hidden="true"
    >
      <FileGlyph />
      {/* The list row already spells the type out beside the name, and an
          extension chip at this size is a smudge — colour and glyph carry it. */}
      {!compact && <span className={styles.iconBadge}>{kind.badge}</span>}
    </span>
  );
}

interface DetailPopoverState {
  top: number;
  left: number;
  /** True when the anchor is low on screen and the card is placed above it. */
  above: boolean;
}

/** Delay before the hover card appears — long enough not to fire on a pass-over. */
const POPOVER_DELAY_MS = 220;
const POPOVER_WIDTH = 264;

/**
 * Hover/focus detail card for one attachment.
 *
 * Fixed-positioned in a portal against the anchor's viewport rect: the grid
 * tiles live inside a scrolling modal body with `overflow` clipping, so an
 * in-flow popover would be cut off by the first tile it overflowed. Any scroll
 * dismisses it rather than trying to keep a stale rect in sync.
 */
function useDetailPopover(attachment: AttachmentDraft, jobId?: string) {
  const [placement, setPlacement] = useState<DetailPopoverState | null>(null);
  const anchorRef = useRef<HTMLElement | null>(null);
  const timerRef = useRef<number | null>(null);

  const clearTimer = (): void => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const close = (): void => {
    clearTimer();
    setPlacement(null);
  };

  const openFrom = (element: HTMLElement, delay: number): void => {
    anchorRef.current = element;
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      const rect = element.getBoundingClientRect();
      const above = rect.bottom + 200 > window.innerHeight;
      setPlacement({
        top: above ? rect.top - 8 : rect.bottom + 8,
        left: Math.min(Math.max(rect.left, 8), Math.max(window.innerWidth - POPOVER_WIDTH - 8, 8)),
        above,
      });
    }, delay);
  };

  useEffect(() => clearTimer, []);

  useEffect(() => {
    if (!placement) return;
    const dismiss = (): void => setPlacement(null);
    // Capture, because the scroll happens on the modal body, not the window.
    window.addEventListener("scroll", dismiss, true);
    window.addEventListener("resize", dismiss);
    return () => {
      window.removeEventListener("scroll", dismiss, true);
      window.removeEventListener("resize", dismiss);
    };
  }, [placement]);

  const kind = attachmentKind(attachment.name, attachment.type);

  const anchorProps = {
    onMouseEnter: (event: { currentTarget: HTMLElement }) => openFrom(event.currentTarget, POPOVER_DELAY_MS),
    onMouseLeave: close,
    // Keyboard users get the same card, without the dwell delay.
    onFocus: (event: { currentTarget: HTMLElement }) => openFrom(event.currentTarget, 0),
    onBlur: close,
  };

  const popover =
    placement &&
    createPortal(
      <div
        className={styles.popover}
        role="tooltip"
        style={{
          top: placement.top,
          left: placement.left,
          width: POPOVER_WIDTH,
          transform: placement.above ? "translateY(-100%)" : undefined,
        }}
      >
        <p className={styles.popName}>{attachment.name}</p>
        <dl className={styles.popList}>
          <dt>Type</dt>
          <dd>{kind.label}</dd>
          <dt>Format</dt>
          <dd className={styles.popMono}>{attachment.type || "unknown"}</dd>
          <dt>Size</dt>
          <dd>{formatFileSize(attachment.size)}</dd>
          <dt>Added</dt>
          <dd>
            {attachment.persisted
              ? formatAttachmentDate(attachment.uploadedAt)
              : "Not uploaded yet — saves with the job"}
          </dd>
          <dt>Uploaded by</dt>
          <dd>{attachment.persisted ? attachment.uploadedBy || "Unknown" : "You (pending)"}</dd>
        </dl>
        {attachment.persisted && jobId && isViewableImage(attachment, jobId) && (
          <p className={styles.popHint}>Click to open in the viewer</p>
        )}
      </div>,
      document.body,
    );

  return { anchorProps, popover };
}

/* ---------- glyphs ---------- */

/** Page-with-folded-corner silhouette behind the extension badge. */
function FileGlyph() {
  return (
    <svg viewBox="0 0 32 40" width="34" height="42" aria-hidden="true" focusable="false">
      <path
        d="M4 3.5h15L28 12v24.5a1.5 1.5 0 0 1-1.5 1.5h-22A1.5 1.5 0 0 1 3 36.5v-31A2 2 0 0 1 4 3.5Z"
        fill="currentColor"
        opacity="0.14"
      />
      <path
        d="M4.5 2.5h14.2L28 11.8v24.7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4.5a2 2 0 0 1 1.5-2Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M18.5 2.6v9.2H28" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

function GridIcon() {
  return (
    <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false">
      <path
        d="M2.5 2.5h4v4h-4zM9.5 2.5h4v4h-4zM2.5 9.5h4v4h-4zM9.5 9.5h4v4h-4z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
      />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false">
      <path
        d="M2.5 4h11M2.5 8h11M2.5 12h11"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
      <path
        d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M4 16v2.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">
      <path d="M4 4l8 8M12 4l-8 8" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}
